// lib/canvas-classify.js
// ═══════════════════════════════════════════════════════════
// Artifact classification helpers shared across the canvas panel
// and any future consumer that needs to know what kind of preview
// a given artifact gets.
// ═══════════════════════════════════════════════════════════

// Build the same labels the journey map uses (F1, F2…) so the canvas
// tile and the journey-map node visibly share an identity. Only files
// here, so the prefix is always F.
export function buildFileLabels(fileNodes) {
  const ordered = [...(fileNodes || [])].sort((a, b) => {
    const at = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (at !== bt) return at - bt;
    return (a.id || '').localeCompare(b.id || '');
  });
  const labels = {};
  ordered.forEach((n, i) => { labels[n.id] = `F${i + 1}`; });
  return labels;
}

export const IMAGE_EXT_RE = /\.(png|jpg|jpeg|gif|webp|svg|bmp|heic)(?:[?#]|$)/i;
export const HTML_EXT_RE = /\.(html?|htm)(?:[?#]|$)/i;
// Heuristic for inline HTML content — Shift's artifact detector saves
// fenced ```html blocks AND raw <!DOCTYPE html> blocks as artifacts
// with type 'html'. This regex catches the raw form when the type
// metadata is missing or wrong.
export const HTML_CONTENT_RE = /^\s*(<!DOCTYPE\s+html|<html[\s>])/i;
export const SVG_CONTENT_RE = /^\s*<svg[\s>]/i;

// Detect what kind of preview a given artifact gets. Falls back to the
// file node title (which is the original uploaded filename) when the
// artifact metadata doesn't carry an explicit kind. This is necessary
// because the save-artifact API never writes is_image, so we have to
// rediscover image-ness from the URL or the filename extension.
export function classifyArtifact(artifact, node) {
  const type = (artifact?.artifact_type || '').toLowerCase();
  const content = artifact?.content || '';
  const titleHint = node?.title || '';

  if (artifact?.is_image) return 'image';
  if (IMAGE_EXT_RE.test(content)) return 'image';
  if (IMAGE_EXT_RE.test(titleHint)) return 'image';

  if (type === 'upload' && /^https?:\/\//.test(content)) return 'link';

  // SVG detection — inline SVG source rendered as a visual.
  // Checked before HTML so <svg> content doesn't get wrapped in an iframe.
  if (type === 'svg') return 'svg';
  if (/\.svg$/i.test(titleHint)) return 'svg';
  if (typeof content === 'string' && SVG_CONTENT_RE.test(content)) return 'svg';

  // HTML mockup detection — render in a sandboxed iframe so the user
  // sees the actual page Shift generated, not the source code dump.
  // Three signals: explicit type, .html extension, or content that
  // starts with <!DOCTYPE / <html>.
  if (type === 'html' || type === 'htm') return 'html';
  if (HTML_EXT_RE.test(titleHint)) return 'html';
  if (typeof content === 'string' && HTML_CONTENT_RE.test(content)) return 'html';

  if (['code','js','jsx','ts','tsx','css','json','py','sql'].includes(type)) return 'code';
  if (!artifact) return 'unknown';
  return 'doc';
}

// ── Download artifact as a file ──────────────────────────────
// Builds a blob from the artifact content and triggers a download.
// For image URLs, opens in a new tab instead.
export function downloadArtifact(artifact, node) {
  if (!artifact?.content) return;
  const kind = classifyArtifact(artifact, node);
  const title = (node?.title || 'artifact').replace(/[^a-zA-Z0-9_-]/g, '_');
  const extMap = { html: 'html', svg: 'svg', code: 'txt', doc: 'md', image: '', link: 'txt' };
  const codeExt = (artifact.artifact_type || '').toLowerCase();
  const langExts = { javascript: 'js', js: 'js', typescript: 'ts', ts: 'ts', python: 'py', py: 'py', css: 'css', json: 'json', sql: 'sql', html: 'html', jsx: 'jsx', tsx: 'tsx' };
  const ext = kind === 'code' ? (langExts[codeExt] || codeExt || 'txt') : (extMap[kind] || 'txt');
  const mimeMap = { html: 'text/html', svg: 'image/svg+xml', md: 'text/markdown', js: 'text/javascript', py: 'text/x-python', css: 'text/css', json: 'application/json', sql: 'text/x-sql', txt: 'text/plain' };
  const mime = mimeMap[ext] || 'text/plain';
  // For images (URLs), open in new tab instead
  if (kind === 'image' && /^https?:\/\//.test(artifact.content)) {
    window.open(artifact.content, '_blank');
    return;
  }
  const blob = new Blob([artifact.content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title}.${ext}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
